import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export type ReportType = 
  | 'sales'
  | 'inventory'
  | 'payment'
  | 'register'
  | 'user'
  | 'category'
  | 'gift_card'
  | 'store_credit'
  | 'adjustment'
  | 'custom';

export type ScheduleFrequency = 'daily' | 'weekly' | 'monthly';

export interface SavedReport {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  report_type: ReportType;
  configuration: Record<string, any>;
  chart_type: string | null;
  created_by: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  creator?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  schedule?: ReportSchedule;
  is_favorite?: boolean;
}

export interface ReportSchedule {
  id: string;
  saved_report_id: string;
  frequency: ScheduleFrequency;
  day_of_week: number | null;
  day_of_month: number | null;
  time_of_day: string;
  timezone: string;
  format: string;
  recipients: string[];
  is_active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReportShare {
  id: string;
  saved_report_id: string;
  shared_with_user_id: string;
  can_edit: boolean;
  can_delete: boolean;
  can_schedule: boolean;
  shared_by: string;
  shared_at: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface ReportRun {
  id: string;
  saved_report_id: string;
  run_type: 'manual' | 'scheduled';
  run_by: string | null;
  schedule_id: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at: string | null;
  result_row_count: number | null;
  result_file_url: string | null;
  error_message: string | null;
  created_at: string;
}

// Fetch all saved reports for the user
export function useSavedReports() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['saved-reports', profile?.organization_id],
    queryFn: async () => {
      // Fetch reports
      const { data: reports, error } = await supabase
        .from('saved_reports')
        .select(`
          *,
          report_schedules(*)
        `)
        .order('updated_at', { ascending: false });

      if (error) {
        // Table may not exist yet
        if (error.code === '42P01') {
          return [];
        }
        throw error;
      }

      // Fetch creator info
      const creatorIds = [...new Set((reports || []).map(r => r.created_by))];
      const { data: creators } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', creatorIds);

      // Fetch favorites
      const { data: favorites } = await supabase
        .from('report_favorites')
        .select('saved_report_id')
        .eq('user_id', profile!.id);

      const favoriteIds = new Set((favorites || []).map(f => f.saved_report_id));
      const creatorMap = new Map((creators || []).map(c => [c.id, c]));

      return (reports || []).map(r => ({
        ...r,
        creator: creatorMap.get(r.created_by),
        schedule: r.report_schedules?.[0] || null,
        is_favorite: favoriteIds.has(r.id),
      })) as SavedReport[];
    },
    enabled: !!profile?.organization_id,
  });
}

// Create a new saved report
export function useCreateSavedReport() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      report_type: ReportType;
      configuration?: Record<string, any>;
      chart_type?: string;
      is_public?: boolean;
    }) => {
      const { data: report, error } = await supabase
        .from('saved_reports')
        .insert({
          organization_id: profile!.organization_id,
          created_by: profile!.id,
          name: data.name,
          description: data.description || null,
          report_type: data.report_type,
          configuration: data.configuration || {},
          chart_type: data.chart_type || null,
          is_public: data.is_public || false,
        })
        .select()
        .single();

      if (error) throw error;
      return report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Update a saved report
export function useUpdateSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      name?: string;
      description?: string;
      report_type?: ReportType;
      configuration?: Record<string, any>;
      chart_type?: string;
      is_public?: boolean;
    }) => {
      const { data: report, error } = await supabase
        .from('saved_reports')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Delete a saved report
export function useDeleteSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('saved_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Toggle favorite
export function useToggleFavorite() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ reportId, isFavorite }: { reportId: string; isFavorite: boolean }) => {
      if (isFavorite) {
        // Remove favorite
        const { error } = await supabase
          .from('report_favorites')
          .delete()
          .eq('saved_report_id', reportId)
          .eq('user_id', profile!.id);

        if (error) throw error;
      } else {
        // Add favorite
        const { error } = await supabase
          .from('report_favorites')
          .insert({
            saved_report_id: reportId,
            user_id: profile!.id,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Create or update schedule
export function useUpsertSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      saved_report_id: string;
      frequency: ScheduleFrequency;
      day_of_week?: number;
      day_of_month?: number;
      time_of_day: string;
      timezone?: string;
      format?: string;
      recipients: string[];
      is_active?: boolean;
    }) => {
      // Check if schedule exists
      const { data: existing } = await supabase
        .from('report_schedules')
        .select('id')
        .eq('saved_report_id', data.saved_report_id)
        .single();

      if (existing) {
        // Update
        const { data: schedule, error } = await supabase
          .from('report_schedules')
          .update({
            frequency: data.frequency,
            day_of_week: data.day_of_week ?? null,
            day_of_month: data.day_of_month ?? null,
            time_of_day: data.time_of_day,
            timezone: data.timezone || 'UTC',
            format: data.format || 'pdf',
            recipients: data.recipients,
            is_active: data.is_active ?? true,
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return schedule;
      } else {
        // Create
        const { data: schedule, error } = await supabase
          .from('report_schedules')
          .insert({
            saved_report_id: data.saved_report_id,
            frequency: data.frequency,
            day_of_week: data.day_of_week ?? null,
            day_of_month: data.day_of_month ?? null,
            time_of_day: data.time_of_day,
            timezone: data.timezone || 'UTC',
            format: data.format || 'pdf',
            recipients: data.recipients,
            is_active: data.is_active ?? true,
          })
          .select()
          .single();

        if (error) throw error;
        return schedule;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Delete schedule
export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const { error } = await supabase
        .from('report_schedules')
        .delete()
        .eq('id', scheduleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Toggle schedule active/inactive
export function useToggleSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scheduleId, isActive }: { scheduleId: string; isActive: boolean }) => {
      const { data, error } = await supabase
        .from('report_schedules')
        .update({ is_active: isActive })
        .eq('id', scheduleId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Get report shares
export function useReportShares(reportId: string | null) {
  return useQuery({
    queryKey: ['report-shares', reportId],
    queryFn: async () => {
      if (!reportId) return [];

      const { data, error } = await supabase
        .from('report_shares')
        .select(`
          *,
          profiles:shared_with_user_id(id, first_name, last_name, role)
        `)
        .eq('saved_report_id', reportId);

      if (error) throw error;

      return (data || []).map(s => ({
        ...s,
        user: s.profiles,
      })) as ReportShare[];
    },
    enabled: !!reportId,
  });
}

// Share report with user
export function useShareReport() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      saved_report_id: string;
      shared_with_user_id: string;
      can_edit?: boolean;
      can_delete?: boolean;
      can_schedule?: boolean;
    }) => {
      const { data: share, error } = await supabase
        .from('report_shares')
        .insert({
          saved_report_id: data.saved_report_id,
          shared_with_user_id: data.shared_with_user_id,
          shared_by: profile!.id,
          can_edit: data.can_edit || false,
          can_delete: data.can_delete || false,
          can_schedule: data.can_schedule || false,
        })
        .select()
        .single();

      if (error) throw error;
      return share;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', variables.saved_report_id] });
    },
  });
}

// Remove share
export function useRemoveShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shareId, reportId }: { shareId: string; reportId: string }) => {
      const { error } = await supabase
        .from('report_shares')
        .delete()
        .eq('id', shareId);

      if (error) throw error;
      return reportId;
    },
    onSuccess: (reportId) => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', reportId] });
    },
  });
}

// Run report
export function useRunReport() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (reportId: string) => {
      // Create run record
      const { data: run, error } = await supabase
        .from('report_runs')
        .insert({
          saved_report_id: reportId,
          run_type: 'manual',
          run_by: profile!.id,
          status: 'running',
        })
        .select()
        .single();

      if (error) throw error;

      // In a real implementation, this would trigger the actual report generation
      // For now, we'll just simulate completion
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update to completed
      const { data: completed, error: updateError } = await supabase
        .from('report_runs')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          result_row_count: Math.floor(Math.random() * 1000) + 1,
        })
        .eq('id', run.id)
        .select()
        .single();

      if (updateError) throw updateError;
      return completed;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
  });
}

// Get report runs
export function useReportRuns(reportId: string | null) {
  return useQuery({
    queryKey: ['report-runs', reportId],
    queryFn: async () => {
      if (!reportId) return [];

      const { data, error } = await supabase
        .from('report_runs')
        .select('*')
        .eq('saved_report_id', reportId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data || []) as ReportRun[];
    },
    enabled: !!reportId,
  });
}

// Helper to get report type label
export function getReportTypeLabel(type: ReportType): string {
  const labels: Record<ReportType, string> = {
    sales: 'Sales',
    inventory: 'Inventory',
    payment: 'Payment',
    register: 'Register',
    user: 'User',
    category: 'Category',
    gift_card: 'Gift Card',
    store_credit: 'Store Credit',
    adjustment: 'Adjustment',
    custom: 'Custom',
  };
  return labels[type] || type;
}

// Helper to format schedule description
export function formatSchedule(schedule: ReportSchedule | null): string {
  if (!schedule) return 'Not scheduled';

  const time = schedule.time_of_day?.slice(0, 5) || '08:00';

  if (schedule.frequency === 'daily') {
    return `Daily at ${time}`;
  } else if (schedule.frequency === 'weekly') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days[schedule.day_of_week || 0];
    return `Weekly on ${day} at ${time}`;
  } else if (schedule.frequency === 'monthly') {
    return `Monthly on day ${schedule.day_of_month || 1} at ${time}`;
  }

  return 'Scheduled';
}

